import { createAsyncAction } from 'async-selector-kit'
import { API_PATH_SUBSCRIPTIONS_CREATE, PORTAL_ROUTE } from '../../../../config'
import { onIsLoading } from '../../../../utils/onLoading'
import { Profile } from '../../types'
import { getApiConfig, getLocalServerURL } from '../../../../utils/getApiConfig'
import {
  getAllRows,
  getSelectedIndividualUnits,
} from '../../../SelectUnits/selectors'
import { getUnitNumbers } from '../../../../utils/getUnitNumbers'
import cloneDeep from 'lodash/cloneDeep'
import { getUnitsIndividual, getUnitsMaster } from '../../../Units/selectors'
import { RootState } from '../../../../store'
import { updateUnitMaster } from '../../../Units/commands/master'
import { SelectUnit } from '../../../SelectUnits/types'
import { updateUnitIndividual } from '../../../Units/commands/individual'
import { getIsUnitsModalOpen } from '../../../Portal/selectors'
import { actions as portalActions } from '../../../Portal/reducer'
import { getPortalBootstrapForce } from '../../selectors/api/bootstrap'
import { onError } from '../../../../utils/onError'
import { actions as selectUnitsActions } from '../../../SelectUnits/reducer'
import { clientLogger } from '../../../../utils/observability'

interface SubscriptionCreateBody {
  lastName: string
  customerProfileId: number
  customerPaymentProfileId: number
  customerAddressId: number
  unitNumber: string
  email: string
  size: string
  description: string
  total: number
}

export type StoreLike = {
  getState: () => RootState
  dispatch: (action: any) => void
}

/**
 * Core subscription create logic. Exported for testing both branches (modal vs navigate, success vs error).
 */
export async function executeCreateSubscription(
  store: StoreLike,
  profile: Profile
): Promise<void> {
  const PATH = getLocalServerURL(API_PATH_SUBSCRIPTIONS_CREATE)
  onIsLoading(true)

  try {
    const individualUnits = getUnitsIndividual(store.getState() as RootState)
    const masterUnits = getUnitsMaster(store.getState() as RootState)
    const selectedIndividualUnits = getSelectedIndividualUnits(
      store.getState() as RootState
    )
    const isUnitsModalOpen = getIsUnitsModalOpen(store.getState() as RootState)
    const allRows = getAllRows(store.getState() as RootState)
    const units = getUnitNumbers({ allRows, isCreate: true })
    const filteredUnits = selectedIndividualUnits.filter((x) =>
      units.some((y) => y.unitNumber === x.unitNumber)
    )
    const _units = filteredUnits.map((x) => {
      const masterId = individualUnits?.find((iu) => iu.id === x.id)?.masterId
      const matchingUnit = masterUnits.find((u) => u.id === masterId)
      if (matchingUnit != null) {
        x = cloneDeep(x)
        x.price = matchingUnit.price
        x.masterId = masterId
      }
      if (x.isCovered === undefined) {
        x.isCovered = false
      }
      if (x.isClimate === undefined) {
        x.isClimate = false
      }
      return x
    })

    clientLogger.action('subscription_create_started', {
      customerProfileId: profile.customerProfileId,
      unitCount: _units.length,
      units: _units.map((unit) => ({
        unitNumber: unit.unitNumber,
        size: unit.size,
        masterId: unit.masterId,
      })),
    })

    const promises = _units.map(async (x) => {
      const model: SubscriptionCreateBody = {
        customerProfileId: profile.customerProfileId,
        customerPaymentProfileId: profile.customerPaymentProfileId,
        customerAddressId: profile.customerAddressId,
        lastName: profile.lastName,
        email: profile.email,
        size: x.size,
        total: x.price,
        unitNumber: String(x.unitNumber),
        description: `${x.size}/${x.unitNumber}`,
      }

      const config = await getApiConfig({ body: model })
      if (config == null) {
        return Promise.reject(new Error('Invalid configuration'))
      }

      const response = await fetch(PATH, config)
      if (!response.ok) {
        store.dispatch(selectUnitsActions.onResetState())
        clientLogger.warn('subscription_create_http_failed', {
          customerProfileId: profile.customerProfileId,
          unitNumber: x.unitNumber,
          status: response.status,
        })
        onError('Failed to create subscription')
        return Promise.reject(new Error('Failed API response'))
      }

      const data = await response.json()
      if (data?.messages?.resultCode === 'Ok') {
        clientLogger.info('subscription_create_succeeded', {
          customerProfileId: profile.customerProfileId,
          unitNumber: x.unitNumber,
          subscriptionId: data.subscriptionId,
        })
        try {
          updateUnitMaster(String(x.masterId))
        } catch (e) {
          clientLogger.error('subscription_create_master_sync_failed', {
            unitNumber: x.unitNumber,
            masterId: x.masterId,
            error: e,
          })
          onError('Failed to update firebase')
        }
        try {
          updateUnitIndividual({
            ...model,
            ...{ subscriptionId: data.subscriptionId },
          } as unknown as SelectUnit)
        } catch (e) {
          clientLogger.error('subscription_create_individual_sync_failed', {
            unitNumber: x.unitNumber,
            subscriptionId: data.subscriptionId,
            error: e,
          })
          onError('Failed to update firebase')
        }
        return Promise.resolve()
      } else {
        const errorMessage =
          data?.messages?.message?.[0]?.text || 'Unknown error'
        store.dispatch(selectUnitsActions.onResetState())
        clientLogger.warn('subscription_create_rejected', {
          customerProfileId: profile.customerProfileId,
          unitNumber: x.unitNumber,
          errorMessage,
        })
        onError(errorMessage)
        return Promise.reject(new Error(errorMessage))
      }
    })

    await Promise.all(promises)
    store.dispatch(selectUnitsActions.onResetState())
    await getPortalBootstrapForce(store.getState() as RootState)

    if (isUnitsModalOpen) {
      store.dispatch(portalActions.onOpenUnitsModal(false))
    } else {
      window.navigate != null && window.navigate(PORTAL_ROUTE)
    }
  } catch (error) {
    clientLogger.error('subscription_create_failed', {
      customerProfileId: profile.customerProfileId,
      error,
    })
    onError(
      error instanceof Error ? error.message : 'An unexpected error occurred'
    )
  } finally {
    onIsLoading(false)
  }
}

export const [createSubscription, _, createSubscriptionError] =
  createAsyncAction(
    {
      id: 'create-subscription',
      async: (store) => async (profile: Profile) => {
        await executeCreateSubscription(store, profile)
      },
    },
    []
  )
